const DOCK_SCROLL_OFFSET = 24;

const getSectionId = (link: HTMLAnchorElement): string | null => {
  const hash = link.hash;

  if (!hash || !hash.startsWith('#')) {
    return null;
  }

  return decodeURIComponent(hash.slice(1));
};

const setActiveLink = (links: HTMLAnchorElement[], activeLink: HTMLAnchorElement | null): void => {
  links.forEach((link) => {
    const isActive = link === activeLink;

    link.classList.toggle('is-active', isActive);

    if (isActive) {
      link.setAttribute('aria-current', 'true');
    } else {
      link.removeAttribute('aria-current');
    }
  });
};

const getCurrentLink = (
  sectionPairs: Array<{ link: HTMLAnchorElement; section: HTMLElement }>
): HTMLAnchorElement | null => {
  const viewportAnchor = window.innerHeight * 0.45;
  let currentPair = sectionPairs[0] ?? null;

  sectionPairs.forEach((pair) => {
    if (pair.section.getBoundingClientRect().top <= viewportAnchor) {
      currentPair = pair;
    }
  });

  return currentPair?.link ?? null;
};

export const initOnepageNavigation = (): void => {
  document.querySelectorAll<HTMLElement>('[data-onepage-navigation]').forEach((navigation) => {
    const mobileLayout = window.matchMedia('(max-width: 700px)');
    const links = Array.from(
      navigation.querySelectorAll<HTMLAnchorElement>('[data-onepage-navigation-link]')
    );
    const sectionPairs = links
      .map((link) => {
        const sectionId = getSectionId(link);
        const section = sectionId ? document.getElementById(sectionId) : null;

        return section ? { link, section } : null;
      })
      .filter((pair): pair is { link: HTMLAnchorElement; section: HTMLElement } => pair !== null);

    if (sectionPairs.length === 0) {
      return;
    }

    const updateNavigationState = (): void => {
      navigation.classList.toggle(
        'is-docked',
        mobileLayout.matches || window.scrollY > DOCK_SCROLL_OFFSET
      );
      setActiveLink(links, getCurrentLink(sectionPairs));
    };

    let animationFrame = 0;
    const requestNavigationUpdate = (): void => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(updateNavigationState);
    };

    links.forEach((link) => {
      link.addEventListener('click', () => {
        setActiveLink(links, link);
      });
    });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Node) || navigation.contains(event.target)) {
        return;
      }

      const activeElement = document.activeElement;

      if (activeElement instanceof HTMLElement && navigation.contains(activeElement)) {
        activeElement.blur();
      }

      navigation.classList.add('is-docked');
    });

    window.addEventListener('scroll', requestNavigationUpdate, { passive: true });
    window.addEventListener('resize', requestNavigationUpdate);
    mobileLayout.addEventListener('change', requestNavigationUpdate);
    updateNavigationState();
  });
};
